import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  useTheme,
  MenuItem,
  FormControl,
  Select,
  Divider,
  IconButton,
  FormHelperText,
  Slider,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Avatar,
  alpha,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ExpandMore as ExpandMoreIcon,
  Timer as TimerIcon,
  Public as PublicIcon,
  Code as CodeIcon,
  DeveloperBoard as DeveloperBoardIcon,
  Add,
  Remove,
  Computer as ComputerIcon,
  Dns as DnsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Webhook as WebhookIcon,
  Telegram as TelegramIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  CheckCircle,
  Error as ErrorIcon,
  Language,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import Swal from 'sweetalert2'
import api from '../../api/axiosInstance'
import { useSelector } from 'react-redux'
import { selectUser } from '../../redux/features/auth/authSelectors'
import { getUserPlan } from '../../utils/plan'

// Country flags data (emoji flags)
const REGIONS = [
  { code: 'US', name: 'United States', flag: '🇺🇸', coordinates: [-95.7129, 37.0902] },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', coordinates: [10.4515, 51.1657] },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', coordinates: [-0.1276, 51.5074] },
  { code: 'FR', name: 'France', flag: '🇫🇷', coordinates: [2.3522, 48.8566] },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', coordinates: [103.8198, 1.3521] },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', coordinates: [151.2093, -33.8688] },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', coordinates: [139.6503, 35.6762] },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', coordinates: [-47.8825, -15.7942] },
  { code: 'IN', name: 'India', flag: '🇮🇳', coordinates: [77.2090, 28.6139] },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', coordinates: [-75.6972, 45.4215] },
]

// Notification channels
const NOTIFICATION_CHANNELS = [
  { id: 'email', label: 'Email', icon: <EmailIcon />, color: '#ea4335' },
  { id: 'sms', label: 'SMS', icon: <SmsIcon />, color: '#34a853' },
  { id: 'webhook', label: 'Webhook', icon: <WebhookIcon />, color: '#4285f4' },
  { id: 'telegram', label: 'Telegram', icon: <TelegramIcon />, color: '#0088cc' },
]

// Auth types
const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'apikey', label: 'API Key' },
]

// Modern styled components
const GlowCard = ({ children, active, ...props }) => {
  const theme = useTheme()
  
  return (
    <Card
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8
        )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${
          active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)
        }`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: active
            ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
            : 'transparent',
        },
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Card>
  )
}

const ModernNewMonitorPage = ({ update = false }) => {
  const userReducer = useSelector(selectUser)
  const plan = getUserPlan(userReducer)
  const params = useParams()
  const navigate = useNavigate()
  const theme = useTheme()

  // States
  const [monitorType, setMonitorType] = useState('http')
  const [selectedRegions, setSelectedRegions] = useState([])
  const [notificationChannels, setNotificationChannels] = useState(['email'])
  const [authType, setAuthType] = useState('none')
  const [validateOnChangeState, setValidateOnChangeState] = useState(false)

  // Monitor types configuration
  const monitorTypes = [
    {
      id: 'http',
      icon: <PublicIcon />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      title: 'HTTP(S)',
      info: 'Web sitelerini ve API\'leri izleyin',
      access: plan?.features.http_monitoring,
    },
    {
      id: 'ping',
      icon: <ComputerIcon />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      title: 'PING',
      info: 'ICMP protokolü ile ağ bağlantısını izleyin',
      access: plan?.features.ping_monitoring,
    },
    {
      id: 'port',
      icon: <DeveloperBoardIcon />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      title: 'PORT',
      info: 'Belirli portları izleyin',
      access: plan?.features.port_monitoring,
    },
    {
      id: 'keyword',
      icon: <CodeIcon />,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      title: 'KEYWORD',
      info: 'Web sayfalarındaki anahtar kelimeleri izleyin',
      access: plan?.features.keyword_monitoring,
    },
    {
      id: 'cronjob',
      icon: <TimerIcon />,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      title: 'CRON JOB',
      info: 'Zamanlanmış işleri izleyin',
      access: plan?.features.cronjob_monitoring,
    },
    {
      id: 'dns',
      icon: <DnsIcon />,
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      title: 'DNS',
      info: 'DNS kayıtlarını ve çözümlemelerini izleyin',
      access: !plan?.features.dns_monitoring,
    },
  ]

  // Formik configuration (simplified for example)
  const formik = useFormik({
    initialValues: {
      name: '',
      host: '',
      method: 'GET',
      interval: 5,
      intervalUnit: 'minutes',
      authType: 'none',
      authToken: '',
      authUsername: '',
      authPassword: '',
      authApiKey: '',
      dnsRecordType: 'A',
      dnsExpectedValue: '',
    },
    onSubmit: (values) => {
      console.log('Form values:', values)
      // Handle form submission
    },
  })

  const { values, errors, handleChange, setFieldValue, handleSubmit } = formik

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ width: '100%' }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          {update ? 'Monitor Güncelle' : 'Yeni Monitor Ekle'}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
          Sistemlerinizi sürekli izleyin ve sorunları anında tespit edin
        </Typography>
      </Box>

      <Divider sx={{ mb: 4, borderColor: alpha(theme.palette.primary.main, 0.2) }} />

      {/* Monitor Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Language sx={{ color: theme.palette.primary.main }} />
          Monitor Tipi Seçin
        </Typography>

        <Grid container spacing={2}>
          {monitorTypes.map((type) => (
            <Grid item xs={12} sm={6} md={4} key={type.id}>
              <GlowCard
                active={monitorType === type.id}
                onClick={() => type.access && setMonitorType(type.id)}
                sx={{ opacity: !type.access ? 0.6 : 1 }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        background: type.gradient,
                      }}
                    >
                      {type.icon}
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {type.title}
                        </Typography>
                        {monitorType === type.id && (
                          <CheckCircle
                            sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: alpha('#fff', 0.7), display: 'block' }}
                      >
                        {type.info}
                      </Typography>
                      {!type.access && (
                        <Chip
                          label="🔒 Paket Yükseltme"
                          size="small"
                          sx={{
                            mt: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            background: alpha(theme.palette.warning.main, 0.2),
                            color: theme.palette.warning.main,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </GlowCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Basic Information */}
      <GlowCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Temel Bilgiler
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monitor Adı"
                name="name"
                value={values.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.5),
                  },
                }}
              />
            </Grid>

            {monitorType !== 'cronjob' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Host / URL"
                  name="host"
                  value={values.host}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  error={!!errors.host}
                  helperText={errors.host}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.5),
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Kontrol Aralığı"
                name="interval"
                value={values.interval}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.5),
                  },
                }}
              >
                {[1, 2, 3, 5, 10, 15, 30, 60].map((min) => (
                  <MenuItem key={min} value={min}>
                    {min} dakika
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {monitorType === 'dns' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="DNS Kayıt Tipi"
                    name="dnsRecordType"
                    value={values.dnsRecordType}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha(theme.palette.background.paper, 0.5),
                      },
                    }}
                  >
                    {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'].map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Beklenen Değer (opsiyonel)"
                    name="dnsExpectedValue"
                    value={values.dnsExpectedValue}
                    onChange={handleChange}
                    placeholder="192.168.1.1 veya example.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha(theme.palette.background.paper, 0.5),
                      },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </GlowCard>

      // Part 2: Regions, Notifications, Auth sections
// Bu kod Part 1'den devam ediyor - PART 2 kısmından önce eklenecek

      {/* Monitoring Regions */}
      <GlowCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Monitoring Bölgeleri
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                İzleme yapılacak bölgeleri seçin (çoklu seçim yapabilirsiniz)
              </Typography>
            </Box>
            {plan?.features.multi_region && (
              <Chip
                label="🔒 Premium"
                size="small"
                sx={{
                  background: alpha(theme.palette.warning.main, 0.2),
                  color: theme.palette.warning.main,
                }}
              />
            )}
          </Box>

          <Autocomplete
            multiple
            options={REGIONS}
            disableCloseOnSelect
            disabled={plan?.features.multi_region}
            value={selectedRegions}
            onChange={(event, newValue) => setSelectedRegions(newValue)}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.5),
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Typography sx={{ fontSize: '1.5rem' }}>{option.flag}</Typography>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                      {option.code}
                    </Typography>
                  </Box>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '1rem' }}>{option.flag}</Typography>
                      <Typography variant="caption">{option.code}</Typography>
                    </Box>
                  }
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.2),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedRegions.length === 0 ? 'Bölge seçin...' : ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.5),
                  },
                }}
              />
            )}
          />

          {selectedRegions.length > 0 && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              sx={{ mt: 3 }}
            >
              <Alert
                severity="info"
                icon={<Language />}
                sx={{
                  background: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              >
                <Typography variant="body2">
                  <strong>{selectedRegions.length}</strong> bölgeden izleme yapılacak
                </Typography>
              </Alert>
            </Box>
          )}
        </CardContent>
      </GlowCard>

      {/* Notification Channels */}
      <GlowCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Bildirim Kanalları
          </Typography>

          <Grid container spacing={2}>
            {NOTIFICATION_CHANNELS.map((channel) => (
              <Grid item xs={12} sm={6} md={3} key={channel.id}>
                <Card
                  component={motion.div}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    if (notificationChannels.includes(channel.id)) {
                      setNotificationChannels(notificationChannels.filter((c) => c !== channel.id))
                    } else {
                      setNotificationChannels([...notificationChannels, channel.id])
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    background: notificationChannels.includes(channel.id)
                      ? alpha(channel.color, 0.1)
                      : alpha(theme.palette.background.paper, 0.5),
                    border: `2px solid ${
                      notificationChannels.includes(channel.id)
                        ? channel.color
                        : alpha(theme.palette.primary.main, 0.2)
                    }`,
                    borderRadius: 2,
                    transition: 'all 0.3s',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        mx: 'auto',
                        mb: 1,
                        background: notificationChannels.includes(channel.id)
                          ? channel.color
                          : alpha(channel.color, 0.2),
                      }}
                    >
                      {channel.icon}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {channel.label}
                    </Typography>
                    {notificationChannels.includes(channel.id) && (
                      <CheckCircle
                        sx={{
                          color: channel.color,
                          fontSize: 16,
                          mt: 0.5,
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Channel-specific settings */}
          <AnimatePresence>
            {notificationChannels.includes('webhook') && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                sx={{ mt: 3 }}
              >
                <TextField
                  fullWidth
                  label="Webhook URL"
                  placeholder="https://your-webhook-url.com"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.5),
                    },
                  }}
                />
              </Box>
            )}

            {notificationChannels.includes('telegram') && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                sx={{ mt: 3 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telegram Bot Token"
                      placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.5),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telegram Chat ID"
                      placeholder="123456789"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.5),
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </AnimatePresence>
        </CardContent>
      </GlowCard>

      {/* Authentication */}
      {(monitorType === 'http' || monitorType === 'keyword') && (
        <GlowCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <LockIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Authentication (Opsiyonel)
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Auth Tipi"
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.5),
                    },
                  }}
                >
                  {AUTH_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <AnimatePresence mode="wait">
                {authType === 'bearer' && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <TextField
                      fullWidth
                      label="Bearer Token"
                      name="authToken"
                      value={values.authToken}
                      onChange={handleChange}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon sx={{ color: theme.palette.primary.main }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.5),
                        },
                      }}
                    />
                  </Grid>
                )}

                {authType === 'basic' && (
                  <>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <TextField
                        fullWidth
                        label="Username"
                        name="authUsername"
                        value={values.authUsername}
                        onChange={handleChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.paper, 0.5),
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      component={motion.div}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <TextField
                        fullWidth
                        type="password"
                        label="Password"
                        name="authPassword"
                        value={values.authPassword}
                        onChange={handleChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.paper, 0.5),
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}

                {authType === 'apikey' && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <TextField
                      fullWidth
                      label="API Key"
                      name="authApiKey"
                      value={values.authApiKey}
                      onChange={handleChange}
                      placeholder="sk_live_..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.5),
                        },
                      }}
                    />
                  </Grid>
                )}
              </AnimatePresence>
            </Grid>

            {authType !== 'none' && (
              <Alert
                severity="warning"
                sx={{
                  mt: 3,
                  background: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                }}
              >
                <Typography variant="body2">
                  <strong>Güvenlik:</strong> Authentication bilgileri şifreli olarak saklanır
                </Typography>
              </Alert>
            )}
          </CardContent>
        </GlowCard>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate(-1)}
          sx={{
            borderColor: alpha(theme.palette.primary.main, 0.5),
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              background: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            px: 4,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
          }}
        >
          {update ? 'Monitor Güncelle' : 'Monitor Oluştur'}
        </Button>
      </Box>
    </Box>
  )
}

export default ModernNewMonitorPage